import { useState, useEffect, useCallback } from 'react';
import { communicationsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  CheckCircle2, Circle, AlertTriangle, Hourglass, Loader2, RefreshCw,
  Building2, ShieldCheck, Hash, MessageSquare, BadgeCheck, Megaphone, Info, ArrowRight, Lock, Shield,
} from 'lucide-react';
import { TrustHubOnboarding } from './TrustHubOnboarding';

/** Scroll the in-tab business-verification section into view. */
const scrollToBusinessVerification = () => {
  const el = document.getElementById('business-verification-section');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

/** Status card row — icon + label + status badge + optional CTA. */
const StatusCard = ({ icon: Icon, title, subtitle, status, statusLabel, action, testid, children }) => {
  const palette = {
    complete: { wrap: 'border-emerald-200 bg-emerald-50/40 dark:bg-emerald-900/10 dark:border-emerald-800/60', icon: 'text-emerald-600 dark:text-emerald-400', badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300', Icon: CheckCircle2 },
    pending:  { wrap: 'border-amber-200 bg-amber-50/40 dark:bg-amber-900/10 dark:border-amber-800/60',     icon: 'text-amber-600 dark:text-amber-400',     badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',     Icon: Hourglass },
    blocked:  { wrap: 'border-red-200 bg-red-50/40 dark:bg-red-900/10 dark:border-red-800/60',             icon: 'text-red-600 dark:text-red-400',         badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',           Icon: AlertTriangle },
    incomplete:{ wrap: 'border-slate-200 bg-slate-50/40 dark:bg-slate-800/40 dark:border-slate-700',       icon: 'text-slate-500 dark:text-slate-400',     badge: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',      Icon: Circle },
    info:     { wrap: 'border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-700',                  icon: 'text-slate-500 dark:text-slate-400',     badge: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',      Icon: Info },
    locked:   { wrap: 'border-slate-200 bg-slate-50/50 dark:bg-slate-900/40 dark:border-slate-700',         icon: 'text-slate-400 dark:text-slate-500',     badge: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',      Icon: Lock },
  };
  const p = palette[status] || palette.incomplete;
  const Status = p.Icon;
  return (
    <Card className={`border ${p.wrap}`} data-testid={testid}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start gap-4">
          <div className={`shrink-0 mt-0.5 ${p.icon}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <h3 className="text-base font-semibold tracking-tight">{title}</h3>
              <Badge className={`gap-1 ${p.badge}`} data-testid={`${testid}-status`}>
                <Status className="w-3 h-3" />
                {statusLabel}
              </Badge>
            </div>
            {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
            {children && <div className="mt-3 text-sm space-y-1.5">{children}</div>}
            {action && <div className="mt-4">{action}</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const KV = ({ k, v, mono = false }) => (
  <div className="flex flex-wrap gap-x-2 text-xs">
    <span className="text-muted-foreground">{k}:</span>
    <span className={mono ? 'font-mono break-all' : ''}>{v ?? <span className="italic text-muted-foreground">(not set)</span>}</span>
  </div>
);

/** Map CNAM lifecycle status → owner-friendly label + UI state. */
function cnamMeta(status) {
  switch (status) {
    case 'approved':       return { label: 'Approved', state: 'complete' };
    case 'submitted':
    case 'pending_review': return { label: 'Pending review', state: 'pending' };
    case 'rejected':       return { label: 'Rejected', state: 'blocked' };
    case 'failed':         return { label: 'Failed', state: 'blocked' };
    case 'draft':
    case 'not_started':
    default:               return { label: 'Not started', state: 'incomplete' };
  }
}

/** Map A2P brand/campaign status → owner-friendly label + UI state. */
function a2pMeta(status) {
  const s = (status || 'none').toLowerCase();
  switch (s) {
    case 'approved':      return { label: 'Approved', state: 'complete' };
    case 'pending':
    case 'in_review':
    case 'in-progress':
    case 'submitted':     return { label: 'In review', state: 'pending' };
    case 'failed':
    case 'rejected':      return { label: 'Rejected', state: 'blocked' };
    case 'none':
    case '':
    default:              return { label: 'Not registered yet', state: 'locked' };
  }
}

export default function TextingSetupTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [submittingBrand, setSubmittingBrand] = useState(false);
  const [campaignFormOpen, setCampaignFormOpen] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await communicationsAPI.textingStatus();
      setData(data);
    } catch (err) {
      toast.error(`Could not load texting setup: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleProvision = async () => {
    if (!window.confirm('This will set up your texting service and link all your phone numbers. Continue?')) return;
    setProvisioning(true);
    try {
      const { data: result } = await communicationsAPI.textingProvision();
      if (result.already_provisioned) {
        toast.info('Already set up — refreshing status.');
      } else {
        toast.success(`Texting service set up — ${result.numbers_associated || 0} number(s) linked.`);
      }
      await load();
    } catch (err) {
      toast.error(`Setup failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setProvisioning(false);
    }
  };

  const handleSubmitBrand = async () => {
    if (!window.confirm('Submit your business for carrier brand registration? Review typically takes 1–3 business days.')) return;
    setSubmittingBrand(true);
    try {
      const { data: result } = await communicationsAPI.submitA2PBrand();
      toast.success(result.is_resubmit ? 'Brand re-submitted for review.' : 'Brand submitted. Awaiting carrier review.');
      await load();
    } catch (err) {
      toast.error(`Brand submission failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setSubmittingBrand(false);
    }
  };

  const handleSubmitCampaign = async (formValues) => {
    try {
      const { data: result } = await communicationsAPI.submitA2PCampaign(formValues);
      toast.success(result.is_resubmit ? 'Campaign re-submitted for review.' : 'Campaign submitted. Awaiting carrier review.');
      setCampaignFormOpen(false);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || d).join('; ') : (detail || err.message);
      toast.error(`Campaign submission failed: ${msg}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="texting-setup-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) return null;

  const { overall, can_provision, provision_blocked_reason, business_info, cnam, numbers, messaging_service, a2p, user_role } = data;
  const cm = cnamMeta(cnam.status);
  const brandMeta = a2pMeta(a2p.brand_status);
  const campaignMeta = a2pMeta(a2p.campaign_status);
  const isOwner = user_role === 'owner';

  // Map overall state → top-banner palette
  const overallPalette = {
    blocked:            { wrap: 'border-red-300 bg-red-50/60 dark:bg-red-900/20 dark:border-red-800',     icon: AlertTriangle, color: 'text-red-700 dark:text-red-300',       title: 'Setup is blocked' },
    incomplete:         { wrap: 'border-amber-300 bg-amber-50/50 dark:bg-amber-900/20 dark:border-amber-800', icon: Hourglass,     color: 'text-amber-700 dark:text-amber-300',   title: 'Texting setup is incomplete' },
    verification_pending:{ wrap: 'border-blue-300 bg-blue-50/60 dark:bg-blue-900/20 dark:border-blue-800', icon: Hourglass,     color: 'text-blue-700 dark:text-blue-300',     title: 'Business verification in progress' },
    ready_to_provision: { wrap: 'border-blue-300 bg-blue-50/60 dark:bg-blue-900/20 dark:border-blue-800', icon: ArrowRight,    color: 'text-blue-700 dark:text-blue-300',     title: 'Ready to set up texting' },
    ms_created_no_numbers: { wrap: 'border-amber-300 bg-amber-50/50 dark:bg-amber-900/20 dark:border-amber-800', icon: AlertTriangle, color: 'text-amber-700 dark:text-amber-300', title: 'Texting set up — but no phone numbers linked' },
    send_ready_pre_a2p: { wrap: 'border-emerald-300 bg-emerald-50/60 dark:bg-emerald-900/20 dark:border-emerald-800', icon: CheckCircle2, color: 'text-emerald-700 dark:text-emerald-300', title: 'Texting is live (carrier compliance recommended next)' },
    a2p_in_progress:    { wrap: 'border-blue-300 bg-blue-50/60 dark:bg-blue-900/20 dark:border-blue-800', icon: Hourglass,     color: 'text-blue-700 dark:text-blue-300',     title: 'Carrier compliance review in progress' },
    fully_compliant:    { wrap: 'border-emerald-300 bg-emerald-50/60 dark:bg-emerald-900/20 dark:border-emerald-800', icon: BadgeCheck,    color: 'text-emerald-700 dark:text-emerald-300', title: 'Texting is fully set up and compliant' },
  };
  const op = overallPalette[overall.state] || overallPalette.incomplete;
  const OverallIcon = op.icon;

  return (
    <div className="space-y-5" data-testid="texting-setup-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Texting Setup</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Configure compliant SMS for your business in one place.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={refreshing} data-testid="refresh-status-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Overall status banner */}
      <Card className={`border ${op.wrap}`} data-testid="overall-banner">
        <CardContent className="py-4 flex items-start gap-3">
          <OverallIcon className={`w-5 h-5 shrink-0 mt-0.5 ${op.color}`} />
          <div className="flex-1 min-w-0">
            <p className={`font-semibold ${op.color}`}>{op.title}</p>
            {overall.next_action && (
              <p className="text-sm mt-1">Next: <span className="font-medium">{overall.next_action}</span></p>
            )}
            {overall.blockers?.length > 0 && (
              <ul className="text-sm mt-2 list-disc list-inside space-y-0.5">
                {overall.blockers.map((b, i) => <li key={i} data-testid={`blocker-${i}`}>{b}</li>)}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Business Verification — moved here so the entire texting setup happens in one place. */}
      <section id="business-verification-section" className="scroll-mt-20" data-testid="business-verification-section">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-4 h-4 text-blue-600" />
              Business verification
            </CardTitle>
          </CardHeader>
          <CardContent>
            <TrustHubOnboarding cnamNumbers={[]} />
          </CardContent>
        </Card>
      </section>

      {/* 1. Business Info */}
      <StatusCard
        testid="card-business-info"
        icon={Building2}
        title="Business information"
        subtitle="We need your legal business details to verify your account for compliant texting."
        status={business_info.complete ? 'complete' : 'incomplete'}
        statusLabel={business_info.complete ? 'Complete' : 'Not started'}
        action={!business_info.complete && (
          <Button size="sm" variant="outline" onClick={scrollToBusinessVerification} data-testid="add-business-info-btn">
            Add business info <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      >
        {business_info.complete && <>
          <KV k="Legal name" v={business_info.business_name} />
          <KV k="Business type" v={business_info.business_type} />
          <KV k="EIN on file" v={business_info.has_ein ? 'Yes' : 'No'} />
          <KV k="Country" v={business_info.country} />
        </>}
      </StatusCard>

      {/* 2. Business Verification (CNAM / Trust Hub) */}
      <StatusCard
        testid="card-cnam"
        icon={ShieldCheck}
        title="Business verification"
        subtitle="Our messaging provider reviews your business documents. Typical turnaround: 1–3 business days."
        status={cm.state}
        statusLabel={cm.label}
        action={cnam.status === 'rejected' || cnam.status === 'failed' ? (
          <Button size="sm" variant="outline" onClick={scrollToBusinessVerification} data-testid="resubmit-cnam-btn">
            Review and resubmit
          </Button>
        ) : (cnam.status === 'not_started' || cnam.status === 'draft' || !cnam.status) && business_info.complete ? (
          <Button size="sm" variant="outline" onClick={scrollToBusinessVerification} data-testid="submit-cnam-btn">
            Submit for verification <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        ) : null}
      >
        {cnam.submitted_at && <KV k="Submitted" v={new Date(cnam.submitted_at).toLocaleDateString()} />}
        {cnam.reviewed_at && <KV k="Last reviewed" v={new Date(cnam.reviewed_at).toLocaleDateString()} />}
        {cnam.error_message && (cnam.status === 'rejected' || cnam.status === 'failed') && (
          <div className="bg-red-100/60 dark:bg-red-900/20 border border-red-200 dark:border-red-800/60 rounded p-2 text-xs text-red-800 dark:text-red-200 mt-1">
            {cnam.error_message}
          </div>
        )}
        {cnam.twilio_customer_profile_sid && <KV k="Verification ID" v={cnam.twilio_customer_profile_sid} mono />}
      </StatusCard>

      {/* 3. Phone Numbers */}
      <StatusCard
        testid="card-numbers"
        icon={Hash}
        title="Phone numbers"
        subtitle="You'll text from these numbers."
        status={numbers.total === 0 ? 'incomplete' : numbers.sms_capable === 0 ? 'blocked' : 'complete'}
        statusLabel={numbers.total === 0 ? 'None added' : `${numbers.sms_capable} SMS-capable / ${numbers.total} total`}
        action={numbers.total === 0 && (
          <Button size="sm" variant="outline" onClick={() => window.location.href = '/phone-numbers'} data-testid="add-number-btn">
            Add a phone number <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      >
        {numbers.list.length > 0 && (
          <div className="space-y-1">
            {numbers.list.map((n) => (
              <div key={n.phone_number} className="flex items-center gap-2 text-xs" data-testid={`number-row-${n.phone_number}`}>
                <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded font-mono">{n.phone_number}</code>
                {n.friendly_name && <span className="text-muted-foreground">— {n.friendly_name}</span>}
                {n.sms_enabled ? <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 text-[10px]">SMS</Badge> : <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-800 text-[10px]">Voice-only</Badge>}
                {messaging_service.sid && n.messaging_service_sid === messaging_service.sid && (
                  <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 text-[10px]">Linked to MS</Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </StatusCard>

      {/* 4. Messaging Service */}
      <StatusCard
        testid="card-messaging-service"
        icon={MessageSquare}
        title="Texting service"
        subtitle="Routes outbound texts through the right number and applies your compliance settings automatically."
        status={
          messaging_service.sid && numbers.associated_with_ms > 0 ? 'complete' :
          messaging_service.sid ? 'pending' :
          can_provision ? 'incomplete' : 'locked'
        }
        statusLabel={
          messaging_service.sid && numbers.associated_with_ms > 0 ? 'Set up' :
          messaging_service.sid ? 'Set up (no numbers linked)' :
          can_provision ? 'Ready to set up' :
          isOwner ? 'Locked' : 'Owner-only'
        }
        action={
          !messaging_service.sid && can_provision ? (
            <Button onClick={handleProvision} disabled={provisioning} data-testid="provision-ms-btn">
              {provisioning && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Set up texting service
            </Button>
          ) : !messaging_service.sid && provision_blocked_reason ? (
            <p className="text-xs text-muted-foreground italic">{provision_blocked_reason}</p>
          ) : null
        }
      >
        {messaging_service.sid && <>
          <KV k="Service ID" v={messaging_service.sid} mono />
          {messaging_service.created_at && <KV k="Set up on" v={new Date(messaging_service.created_at).toLocaleDateString()} />}
          <KV k="Numbers linked" v={`${numbers.associated_with_ms} of ${numbers.total}`} />
        </>}
      </StatusCard>

      {/* 5. A2P Brand */}
      <StatusCard
        testid="card-a2p-brand"
        icon={BadgeCheck}
        title="Carrier brand registration"
        subtitle="US carriers require registered business branding for compliant texting and to avoid filtering. Registration type is auto-selected based on your business type."
        status={brandMeta.state}
        statusLabel={brandMeta.label}
        action={
          isOwner && messaging_service.sid && (
            (!a2p.brand_sid || ['rejected', 'failed'].includes((a2p.brand_status || '').toLowerCase())) ? (
              <Button onClick={handleSubmitBrand} disabled={submittingBrand} data-testid="submit-a2p-brand-btn">
                {submittingBrand && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {a2p.brand_sid ? 'Re-submit brand' : 'Submit brand for review'}
              </Button>
            ) : null
          )
        }
      >
        {!messaging_service.sid && (
          <p className="text-xs text-muted-foreground">Set up your texting service first.</p>
        )}
        {messaging_service.sid && !a2p.brand_sid && isOwner && (
          <p className="text-xs text-muted-foreground">Uses your existing business information. No additional input needed from you — carriers review within 1–3 business days.</p>
        )}
        {a2p.brand_sid && <>
          <KV k="Registration ID" v={a2p.brand_sid} mono />
          {a2p.brand_score != null && <KV k="Score" v={a2p.brand_score} />}
          {a2p.brand_failure_reason && (
            <div className="bg-red-100/60 dark:bg-red-900/20 border border-red-200 dark:border-red-800/60 rounded p-2 text-xs text-red-800 dark:text-red-200 mt-1" data-testid="a2p-brand-rejection-reason">
              <strong>Reason:</strong> {a2p.brand_failure_reason}
            </div>
          )}
        </>}
      </StatusCard>

      {/* 6. A2P Campaign */}
      <StatusCard
        testid="card-a2p-campaign"
        icon={Megaphone}
        title="Carrier campaign registration"
        subtitle="Tell carriers how customers opt in and what you'll text them. We use the Low-Volume Mixed campaign type — the best fit for most service businesses."
        status={campaignMeta.state}
        statusLabel={campaignMeta.label}
        action={
          isOwner && a2p.brand_sid && ['approved', 'pending'].includes((a2p.brand_status || '').toLowerCase()) && (
            (!a2p.campaign_sid || ['rejected', 'failed'].includes((a2p.campaign_status || '').toLowerCase())) ? (
              <Button onClick={() => setCampaignFormOpen(true)} data-testid="open-campaign-form-btn">
                {a2p.campaign_sid ? 'Re-submit campaign' : 'Submit campaign for review'}
              </Button>
            ) : null
          )
        }
      >
        {!a2p.brand_sid && (
          <p className="text-xs text-muted-foreground">Submit your brand registration first.</p>
        )}
        {a2p.campaign_sid && <>
          <KV k="Campaign ID" v={a2p.campaign_sid} mono />
          {a2p.campaign_use_case && <KV k="Type" v={a2p.campaign_use_case.replace(/_/g, ' ').toLowerCase()} />}
          {a2p.campaign_failure_reason && (
            <div className="bg-red-100/60 dark:bg-red-900/20 border border-red-200 dark:border-red-800/60 rounded p-2 text-xs text-red-800 dark:text-red-200 mt-1" data-testid="a2p-campaign-rejection-reason">
              <strong>Reason:</strong> {a2p.campaign_failure_reason}
            </div>
          )}
        </>}
        {campaignFormOpen && (
          <CampaignSubmitForm
            initial={a2p}
            onCancel={() => setCampaignFormOpen(false)}
            onSubmit={handleSubmitCampaign}
          />
        )}
      </StatusCard>
    </div>
  );
}

// ─────────────────────────── Campaign submit form ───────────────────────────
const FormField = ({ label, required, children, hint }) => (
  <div className="space-y-1">
    <label className="text-xs font-medium">{label}{required && <span className="text-red-600 ml-0.5">*</span>}</label>
    {children}
    {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

function CampaignSubmitForm({ initial, onCancel, onSubmit }) {
  const [description, setDescription] = useState(initial?.description || '');
  const [messageFlow, setMessageFlow] = useState(initial?.message_flow || '');
  const [sample1, setSample1] = useState('');
  const [sample2, setSample2] = useState('');
  const [sample3, setSample3] = useState('');
  const [privacyUrl, setPrivacyUrl] = useState(initial?.privacy_policy_url || '');
  const [termsUrl, setTermsUrl] = useState(initial?.terms_url || '');
  const [hasLinks, setHasLinks] = useState(false);
  const [hasPhone, setHasPhone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handle = async (e) => {
    e?.preventDefault?.();
    const samples = [sample1, sample2, sample3].map(s => s.trim()).filter(Boolean);
    if (description.trim().length < 10) { toast.error('Description must be at least 10 characters.'); return; }
    if (messageFlow.trim().length < 20) { toast.error('Opt-in description must be at least 20 characters.'); return; }
    if (samples.length < 2) { toast.error('At least 2 sample messages are required.'); return; }
    if (!privacyUrl.startsWith('http')) { toast.error('Privacy policy must be a public HTTPS URL.'); return; }
    if (!termsUrl.startsWith('http')) { toast.error('Terms URL must be a public HTTPS URL.'); return; }
    setSubmitting(true);
    try {
      await onSubmit({
        description: description.trim(),
        message_flow: messageFlow.trim(),
        message_samples: samples,
        privacy_policy_url: privacyUrl.trim(),
        terms_url: termsUrl.trim(),
        has_embedded_links: hasLinks,
        has_embedded_phone: hasPhone,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handle} className="mt-4 p-4 rounded border bg-slate-50 dark:bg-slate-900/40 space-y-3" data-testid="campaign-submit-form">
      <FormField label="Campaign description" required hint="Plain-English summary of what you'll text customers about.">
        <textarea className="w-full text-sm rounded border bg-background p-2" rows={2} maxLength={500}
          value={description} onChange={(e) => setDescription(e.target.value)} data-testid="campaign-description-input"
          placeholder="e.g. Appointment reminders, job updates, and service confirmations for HVAC repair customers." />
      </FormField>
      <FormField label="How do customers opt in?" required hint="Required by carriers. Describe exactly how customers consent to receive SMS (web form, signed estimate checkbox, verbal opt-in script, etc.).">
        <textarea className="w-full text-sm rounded border bg-background p-2" rows={3}
          value={messageFlow} onChange={(e) => setMessageFlow(e.target.value)} data-testid="campaign-message-flow-input"
          placeholder="e.g. Customers check a box agreeing to receive SMS when signing estimates in person or via our online intake form at https://example.com/contact." />
      </FormField>
      <FormField label="Sample messages" required hint="2–5 example messages you'd actually send.">
        <input className="w-full text-sm rounded border bg-background p-2 mb-1" value={sample1} onChange={(e) => setSample1(e.target.value)} placeholder="Sample 1" data-testid="campaign-sample-1" maxLength={320} />
        <input className="w-full text-sm rounded border bg-background p-2 mb-1" value={sample2} onChange={(e) => setSample2(e.target.value)} placeholder="Sample 2" data-testid="campaign-sample-2" maxLength={320} />
        <input className="w-full text-sm rounded border bg-background p-2" value={sample3} onChange={(e) => setSample3(e.target.value)} placeholder="Sample 3 (optional)" data-testid="campaign-sample-3" maxLength={320} />
      </FormField>
      <FormField label="Privacy policy URL" required hint="Public HTTPS URL.">
        <input type="url" className="w-full text-sm rounded border bg-background p-2" value={privacyUrl} onChange={(e) => setPrivacyUrl(e.target.value)} placeholder="https://example.com/privacy" data-testid="campaign-privacy-url" />
      </FormField>
      <FormField label="Terms & SMS terms URL" required hint="Public HTTPS URL covering messaging terms.">
        <input type="url" className="w-full text-sm rounded border bg-background p-2" value={termsUrl} onChange={(e) => setTermsUrl(e.target.value)} placeholder="https://example.com/terms" data-testid="campaign-terms-url" />
      </FormField>
      <div className="flex flex-wrap gap-4 text-xs">
        <label className="flex items-center gap-2"><input type="checkbox" checked={hasLinks} onChange={(e) => setHasLinks(e.target.checked)} data-testid="campaign-embedded-links" /> Messages contain links</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={hasPhone} onChange={(e) => setHasPhone(e.target.checked)} data-testid="campaign-embedded-phone" /> Messages contain phone numbers</label>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={submitting} data-testid="campaign-cancel-btn">Cancel</Button>
        <Button type="submit" size="sm" disabled={submitting} data-testid="campaign-submit-btn">
          {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Submit for review
        </Button>
      </div>
    </form>
  );
}
